import React, { useMemo } from 'react';
import {
	getFirstEditDescription,
	isEditDescriptionValid,
	isInherited,
	OptionalInherited,
	replaceEditDescriptions,
	resetInheritedArrayValues,
	titlePaths,
} from '@warehouse/title/core';
import { titleEditorStoreSelector, useTitleEditorStore } from '@warehouse/title/domain';
import { TextInput } from '@warehouse/shared/legacy-ui';
import { ReadOnlyTypography } from '@warehouse/shared/ui';
import useTitleAutoSave from '../../../../src/views/title/hooks/useTitleAutoSave/useTitleAutoSave';
import { Description } from '../../core/models/version-intent';
import TitleEditableField from '../../../../src/components/titles/TitleEditableField';

export function EditDescriptionField() {
	const title = useTitleEditorStore(titleEditorStoreSelector.title);
	const { readOnlyValue, setValue, value, commit, error } = useTitleAutoSave<
		OptionalInherited<Description[]>,
		Description
	>({
		path: useMemo(() => titlePaths.descriptions.split('.'), []),
		label: 'Edit Description',
		isRowValid: isEditDescriptionValid,
	});
	const { isInherited: isDescriptionInherited } = isInherited({ inheritedObject: value });
	const firstEditDescription = getFirstEditDescription(value?.displayValue);

	// A blank text is only resolved on commit, so a non-primary edit doesn't re-inherit the parent's description while
	// the user is still retyping.
	const commitEditDescription = () => {
		if (!firstEditDescription.trim()) {
			setValue(resetInheritedArrayValues<Description>([], title));
		}
		commit();
	};

	return (
		<TitleEditableField
			label="Edit Description:"
			path={titlePaths.descriptions}
			direction="row"
			jumpToParentButtonDisabled
			tooltipDisabled
			readComponent={
				<ReadOnlyTypography aria-label="Edit Description" variant={isDescriptionInherited ? 'prefilled' : 'default'}>
					{getFirstEditDescription(readOnlyValue?.displayValue)}
				</ReadOnlyTypography>
			}
			editComponent={
				<TextInput
					ariaLabel="Edit Description"
					fullWidth
					height={38}
					value={firstEditDescription}
					onBlur={commitEditDescription}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							commitEditDescription();
							e.currentTarget.blur();
						}
					}}
					onChange={(e) => {
						const nextValue = e.target.value;
						setValue((prev) => ({ ...prev, displayValue: replaceEditDescriptions(nextValue) }), !!nextValue.trim());
					}}
					variant={isDescriptionInherited ? 'prefilled' : 'default'}
					error={error}
					disabledMargin
				/>
			}
		/>
	);
}
